// © Microsoft Corporation. All rights reserved.

import { AppType, CrsV3Endpoint, GitConfigsFlightPrefix, GitConfigsIndexedDbTreatment, OneServiceFlightPrefix, PeregrineFlightPrefix } from "@msnews/experiences-constants";

/**
 * Git configs supported app types in lower case
 */
export const supportedAppTypes = [AppType.EdgeChromium, AppType.Windows, AppType.WindowsShell];

/**
 * Goes through the array of flights that is given and will filter out any flight that doesn't start with
 * prg-. It then sorts them and returns a new string[].
 * @param flights Array of flights that we will filter through and return only the peregrine
 * @returns specific flights
 */
export function getPeregrineFlights(flights: string[]): string[] {
    if (!flights || flights.length === 0) {
        return flights;
    }

    const cleanedUpFlights = [];
    flights.forEach((item) => {
        const lowercaseItem = item.toLocaleLowerCase().trim();
        // Including specific 1s- prefix to bypass flightID regards OneService's bing.news backfill. Will fill bug to track/remove this.
        if (lowercaseItem.startsWith(PeregrineFlightPrefix) || lowercaseItem.startsWith(OneServiceFlightPrefix)) {
            cleanedUpFlights.push(lowercaseItem);
        }
    });

    return cleanedUpFlights.sort();
}

/**
 * Check if Git configs should be used
 * @param {string} version - CRS version. Example: v2 or v3 (case sensitive)
 * @param {string[]} flights Array of flights. Example: ["testFlight", "prg-abcd"]
 * @param {string} configIndexDocId. Example: cms/api/amp/experienceConfigIndex/BBUsYQa
 * @param {string} apptype. Example: edgeChromium
 * @returns true if git configs are supported
 */
export function shouldUseGitConfigs(version: string, flights: string[], configIndexDocId: string, apptype: string): boolean {
    // If app type is not defined or empty, do not use Git configs.
    // For local dev scenarion of individual experiences running in standalone, app type is blank.
    // Also app type is MUST param in Git configs, if app type is not present, we do not want to attempt Git configs too in prod.
    // If version is not present or it is not v3, do not support Git configs
    if (!apptype || !version || version !== CrsV3Endpoint) {
        return false;
    }

    // Use Git configs if app type is from specified array
    return supportedAppTypes.includes(apptype as AppType)
        // Return true if this is Git configs flight treatment
        || containsGitConfigsFlight(getPeregrineFlights(flights))
        // Return true if this does not have configIndexDocId
        || !configIndexDocId;
}

/**
 * Return true if flight is Git configs treatment by specific flight IDs
 * @param flights array
 */
function containsGitConfigsFlight(flights: string[]): boolean {
    if (flights && flights.length > 0) {
        return flights.some((item) => item.startsWith(GitConfigsIndexedDbTreatment) || item.startsWith(GitConfigsFlightPrefix));
    }
}